var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo } from "react";
import styled from "styled-components";
import { parseUnits } from "ethers/lib/utils";
import { useWeb3Wallet } from "shared/lib/hooks/useWeb3Wallet";
import { getAssetDecimals, getAssetDisplay, getAssetLogo, } from "shared/lib/utils/asset";
import { BaseInput, BaseInputButton, BaseInputContainer, BaseInputLabel, SecondaryText, Title, } from "shared/lib/designSystem";
import colors from "shared/lib/designSystem/colors";
import theme from "shared/lib/designSystem/theme";
import { getAssets, VaultNameOptionMap, } from "shared/lib/constants/constants";
import { formatBigNumber, isPracticallyZero } from "shared/lib/utils/math";
import HelpInfo from "shared/lib/components/Common/HelpInfo";
import TooltipExplanation from "shared/lib/components/Common/TooltipExplanation";
import useVaultActionForm from "../../../../hooks/useVaultActionForm";
import { ActionButton, ConnectWalletButton, } from "shared/lib/components/Common/buttons";
import useConnectWalletModal from "shared/lib/hooks/useConnectWalletModal";
import { getVaultColor } from "shared/lib/utils/vault";
var TransferToVault = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  background: ", "0a;\n  border-radius: ", ";\n  padding: 8px;\n"], ["\n  display: flex;\n  background: ", "0a;\n  border-radius: ", ";\n  padding: 8px;\n"])), colors.primaryText, theme.border.radiusSmall);
var TransferToVaultTitle = styled(Title)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  text-transform: none;\n"], ["\n  text-transform: none;\n"])));
var TransferToVaultLogo = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 56px;\n  height: 56px;\n"], ["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 56px;\n  height: 56px;\n"])));
var InfoData = styled(Title)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  font-size: 14px;\n  line-height: 24px;\n  margin-left: auto;\n"], ["\n  font-size: 14px;\n  line-height: 24px;\n  margin-left: auto;\n"])));
var VaultV1TransferForm = function (_a) {
    var vaultOption = _a.vaultOption, vaultAccount = _a.vaultAccount, transferVaultData = _a.transferVaultData, actionButtonText = _a.actionButtonText, onFormSubmit = _a.onFormSubmit;
    var asset = getAssets(vaultOption);
    var decimals = getAssetDecimals(asset);
    var assetDisplay = getAssetDisplay(asset);
    var color = getVaultColor(vaultOption);
    var _b = useVaultActionForm(vaultOption), handleInputChange = _b.handleInputChange, handleMaxClick = _b.handleMaxClick, vaultActionForm = _b.vaultActionForm, transferData = _b.transferData;
    var active = useWeb3Wallet().active;
    var _c = useConnectWalletModal(), setShowConnectModal = _c[1];
    var isInputNonZero = parseFloat(vaultActionForm.inputAmount) > 0;
    var AssetLogo = getAssetLogo(getAssets(vaultActionForm.receiveVault));
    var error = useMemo(function () {
        if (!active || !isInputNonZero || !transferData) {
            return undefined;
        }
        var inputBigNumber = parseUnits(vaultActionForm.inputAmount, decimals);
        /** Check bigger than available limit */
        if (inputBigNumber.gt(transferVaultData.vaultBalanceInAsset)) {
            return "insufficientLimit";
        }
        /** Check target vault capacity */
        if (inputBigNumber.gt(transferData.availableCapacity)) {
            return "insufficientCapacity";
        }
        /**
         * Check if exceed weekly transfer limit
         */
        if (inputBigNumber.gt(transferVaultData.vaultMaxWithdrawAmount)) {
            return "insufficientWeeklyLimit";
        }
        return undefined;
    }, [
        active,
        decimals,
        isInputNonZero,
        transferData,
        transferVaultData,
        vaultActionForm,
    ]);
    var isZeroCapacity = useMemo(function () {
        return (transferData &&
            isPracticallyZero(transferData.availableCapacity, decimals));
    }, [decimals, transferData]);
    var isZeroAvailableLimit = useMemo(function () {
        return (active &&
            isPracticallyZero(transferVaultData.vaultBalanceInAsset, decimals));
    }, [active, decimals, transferVaultData]);
    var renderActionButton = useCallback(function (error) {
        if (active) {
            return (_jsx(ActionButton, __assign({ disabled: Boolean(error) || !isInputNonZero, onClick: onFormSubmit, className: "py-3", color: color }, { children: actionButtonText }), void 0));
        }
        return (_jsx(ConnectWalletButton, __assign({ onClick: function () { return setShowConnectModal(true); }, type: "button", className: "btn py-3 mb-4" }, { children: "Connect Wallet" }), void 0));
    }, [
        active,
        actionButtonText,
        color,
        isInputNonZero,
        onFormSubmit,
        setShowConnectModal,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(BaseInputLabel, { children: "TRANSFER TO" }, void 0), _jsxs(TransferToVault, __assign({ className: "mt-2" }, { children: [_jsx(TransferToVaultLogo, { children: _jsx(AssetLogo, {}, void 0) }, void 0), _jsxs("div", __assign({ className: "d-flex flex-column justify-content-center ml-2" }, { children: [_jsx(TransferToVaultTitle, { children: Object.keys(VaultNameOptionMap)[Object.values(VaultNameOptionMap).indexOf(vaultActionForm.receiveVault)] }, void 0), _jsxs(SecondaryText, __assign({ fontSize: 12, color: active && (isZeroCapacity || error === "insufficientCapacity")
                                    ? colors.red
                                    : undefined }, { children: ["Available Capacity (", assetDisplay, "):", " ", transferData
                                        ? formatBigNumber(transferData.availableCapacity, decimals)
                                        : "---"] }), void 0)] }), void 0)] }), void 0), _jsxs(BaseInputLabel, __assign({ className: "mt-4" }, { children: ["AMOUNT (", assetDisplay, ")"] }), void 0), _jsxs(BaseInputContainer, __assign({ error: Boolean(error) }, { children: [_jsx(BaseInput, { type: "number", className: "form-control", "aria-label": "ETH", placeholder: "0", value: vaultActionForm.inputAmount, onChange: handleInputChange }, void 0), _jsx(BaseInputButton, __assign({ onClick: handleMaxClick }, { children: "MAX" }), void 0)] }), void 0), _jsxs("div", __assign({ className: "d-flex align-items-center mt-4" }, { children: [_jsx(SecondaryText, { children: "Available Limit" }, void 0), _jsx(TooltipExplanation, { title: "AVAILABLE LIMIT", explanation: "This is equal to the value of your deposits minus the funds you have staked in the " + vaultOption + " staking pool.", renderContent: function (_a) {
                            var ref = _a.ref, triggerHandler = __rest(_a, ["ref"]);
                            return (_jsx(HelpInfo, __assign({ containerRef: ref }, triggerHandler, { children: "i" }), void 0));
                        } }, void 0), _jsxs(InfoData, __assign({ color: isZeroAvailableLimit || error === "insufficientLimit"
                            ? colors.red
                            : undefined }, { children: [active
                                ? formatBigNumber(transferVaultData.vaultBalanceInAsset, decimals)
                                : "---", " ", assetDisplay] }), void 0)] }), void 0), _jsxs("div", __assign({ className: "d-flex align-items-center mt-3 mb-4" }, { children: [_jsx(SecondaryText, { children: "Weekly Transfer Limit" }, void 0), _jsx(TooltipExplanation, { title: "AVAILABLE LIMIT", explanation: "You can withdraw up to " + formatBigNumber(transferVaultData.vaultMaxWithdrawAmount, decimals) + " " + assetDisplay + " before the vault hits its weekly transfer and withdrawal limit (10% of the funds in the vault).", renderContent: function (_a) {
                            var ref = _a.ref, triggerHandler = __rest(_a, ["ref"]);
                            return (_jsx(HelpInfo, __assign({ containerRef: ref }, triggerHandler, { children: "i" }), void 0));
                        } }, void 0), _jsxs(InfoData, __assign({ color: error === "insufficientWeeklyLimit" ? colors.red : undefined }, { children: [formatBigNumber(transferVaultData.vaultMaxWithdrawAmount, decimals), " ", assetDisplay] }), void 0)] }), void 0), renderActionButton(error), isZeroAvailableLimit &&
                vaultAccount &&
                !isPracticallyZero(vaultAccount.totalStakedBalance, decimals) && (_jsxs(SecondaryText, __assign({ className: "d-flex mt-3 text-center", color: colors.red }, { children: ["You have staked all your ", vaultOption, " tokens. You must unstake your", vaultOption, " tokens before you can withdraw from the vault"] }), void 0))] }, void 0));
};
export default VaultV1TransferForm;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
//# sourceMappingURL=VaultV1TransferForm.js.map